import { Button } from '@chessmate/ui';
import { useState } from 'react';
import type { Edition } from '../edition/types.js';
import { communityPack, parseShareToken } from '../lib/community-packs.js';
import { ApiError, marketplace } from '../services/api.js';
import { useCardPackStore } from '../state/card-pack-store.js';
import { useCommunityPackStore } from '../state/community-pack-store.js';

/** Why a share link did not work, in words a person can act on. */
export function shareErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404) return 'This link has expired or was removed. Ask for a new one.';
    if (e.status === 403) return 'This pack is not available here.';
    if (e.status === 0) return 'Cannot reach the pack marketplace right now.';
    return e.message;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Add a community pack from a share link someone sent: paste the link (or just its code), the pack is added to this device
 * and chosen. The images stay on the marketplace; only the pack's description is kept here.
 */
export function CommunityPackAdder({ edition, marketplaceUrl }: { readonly edition: Edition; readonly marketplaceUrl: string }) {
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const add = useCommunityPackStore((s) => s.add);
  const setPack = useCardPackStore((s) => s.setPack);

  async function submit() {
    const token = parseShareToken(link);
    if (!token) {
      setStatus({ ok: false, text: 'That does not look like a pack link. Paste the whole link you were sent.' });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const info = await marketplace.redeemShare(token, edition);
      const pack = communityPack(info, marketplaceUrl, edition);
      add(pack);
      setPack(pack.id);
      setLink('');
      setStatus({ ok: true, text: `Added ${pack.name}${pack.author ? ` by ${pack.author}` : ''}. It is your card pack now.` });
    } catch (e) {
      setStatus({ ok: false, text: shareErrorText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mt-3 flex flex-col gap-2"
      data-testid="community-pack-adder"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="text-base font-bold text-ink" htmlFor="community-pack-link">
        Add a pack from a link
      </label>
      <div className="flex flex-wrap gap-2">
        <input
          id="community-pack-link"
          data-testid="community-pack-link"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder="Paste the link you were sent"
          className="min-h-[48px] min-w-0 flex-1 rounded-xl border border-border bg-surface px-3 text-base text-ink"
        />
        <Button type="submit" disabled={busy || link.trim() === ''} data-testid="community-pack-add">
          {busy ? 'Adding…' : 'Add pack'}
        </Button>
      </div>
      <p className={`text-base font-semibold ${status?.ok ? 'text-fg' : 'text-red-700 dark:text-red-300'}`} role="status" aria-live="polite" data-testid="community-pack-status">
        {status?.text ?? ''}
      </p>
    </form>
  );
}
